
import React, { useState } from 'react';

export type DateRangeOption = '7d' | '30d' | '90d' | 'qtd' | 'ytd' | 'custom';

interface ReportDateSelectorProps {
  value: DateRangeOption;
  onChange: (value: DateRangeOption) => void;
  dark?: boolean;
  startDate?: string;
  endDate?: string;
  onCustomChange?: (start: string, end: string) => void;
}

const ReportDateSelector: React.FC<ReportDateSelectorProps> = ({ 
  value, 
  onChange, 
  dark = false,
  startDate,
  endDate,
  onCustomChange
}) => {
  const options: { label: string; value: DateRangeOption }[] = [
    { label: 'Last 7 Days', value: '7d' },
    { label: 'Last 30 Days', value: '30d' },
    { label: 'Last 90 Days', value: '90d' },
    { label: 'QTD', value: 'qtd' },
    { label: 'YTD', value: 'ytd' },
  ];

  return (
    <div className="flex flex-col sm:flex-row items-end sm:items-center gap-3">
      <div className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-all ${
        dark 
          ? 'bg-white/5 border-white/10 hover:border-white/20' 
          : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm'
      }`}>
        <svg className={`w-3.5 h-3.5 ${dark ? 'text-indigo-400' : 'text-indigo-600'}`} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>
        <select 
          value={value} 
          onChange={(e) => onChange(e.target.value as DateRangeOption)}
          className={`bg-transparent border-none text-[10px] font-black uppercase tracking-widest focus:ring-0 outline-none cursor-pointer ${
            dark ? 'text-slate-300' : 'text-slate-900'
          }`}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} className="text-slate-900 bg-white">{opt.label}</option>
          ))}
          <option value="custom" className="text-slate-900 bg-white">Custom Range...</option>
        </select>
      </div>

      {value === 'custom' && (
        <div className={`flex items-center gap-2 p-1.5 rounded-xl border animate-in slide-in-from-left-2 duration-300 ${
          dark ? 'bg-white/5 border-white/10' : 'bg-indigo-50/50 border-indigo-100'
        }`}>
          <input 
            type="date" 
            value={startDate}
            onChange={(e) => onCustomChange?.(e.target.value, endDate || '')}
            className={`bg-transparent border-none text-[10px] font-bold outline-none focus:ring-0 w-28 ${dark ? 'text-white' : 'text-indigo-900'}`}
          />
          <span className={`text-[10px] font-black ${dark ? 'text-slate-500' : 'text-indigo-300'}`}>TO</span>
          <input 
            type="date" 
            value={endDate}
            onChange={(e) => onCustomChange?.(startDate || '', e.target.value)}
            className={`bg-transparent border-none text-[10px] font-bold outline-none focus:ring-0 w-28 ${dark ? 'text-white' : 'text-indigo-900'}`}
          />
        </div>
      )}
    </div>
  );
};

export default ReportDateSelector;
